<template>
  <div class="content-panel settings-page">
    <div v-if="isAdmin" class="settings-shell">
      <header class="settings-header">
        <div>
          <div class="eyebrow">Bankari Broker</div>
          <h1>Nastavenia</h1>
          <p>
            Centrálne miesto pre údaje, ktoré používajú kalkulačky aj web bankari.sk.
          </p>
        </div>
        <div class="admin-badge">
          <span class="admin-badge-dot"></span>
          Len pre administrátorov
        </div>
      </header>

      <div class="settings-layout">
        <nav class="settings-navigation" aria-label="Sekcie nastavení">
          <button
            class="settings-navigation-item"
            :class="{active: activeSection === 'rates'}"
            type="button"
            @click="selectSection('rates')"
          >
            <span class="navigation-icon">%</span>
            <span>
              <strong>Úrokové sadzby</strong>
              <small>Kalkulačka a web</small>
            </span>
          </button>

          <button
            class="settings-navigation-item"
            :class="{active: activeSection === 'financiers'}"
            type="button"
            @click="selectSection('financiers')"
          >
            <span class="navigation-icon">👤</span>
            <span>
              <strong>Finančníci</strong>
              <small>Účty, dáta a lokality</small>
            </span>
          </button>

          <div class="settings-navigation-item disabled">
            <span class="navigation-icon">★</span>
            <span>
              <strong>Kampane bánk</strong>
              <small>Pripravujeme</small>
            </span>
          </div>

          <button
            class="settings-navigation-item"
            :class="{active: activeSection === 'feedback'}"
            type="button"
            @click="selectSection('feedback')"
          >
            <span class="navigation-icon">!</span>
            <span>
              <strong>Feedback</strong>
              <small>Hlásenia používateľov</small>
            </span>
          </button>
        </nav>

        <main v-if="activeSection === 'rates'" class="settings-card">
          <div class="card-heading">
            <div>
              <div class="section-kicker">Hypotéky</div>
              <h2>Úrokové sadzby</h2>
              <p>
                Zmeny sa zapisujú do API. Web bankari.sk si ich načíta automaticky,
                spravidla do 5 minút.
              </p>
            </div>

            <div class="heading-actions">
              <button
                class="secondary-button"
                type="button"
                :disabled="loading || savingAll || dirtyRateIds.length > 0"
                @click="loadRates"
              >
                Obnoviť
              </button>
              <button
                class="primary-button"
                type="button"
                :disabled="!dirtyRateIds.length || hasInvalidDirtyRates || savingAll"
                @click="saveAll"
              >
                {{ savingAll ? "Ukladám..." : "Uložiť zmeny (" + dirtyRateIds.length + ")" }}
              </button>
            </div>
          </div>

          <div class="rate-guide">
            <div>
              <strong>Základná sadzba</strong>
              <span>Používa ju hypotekárna kalkulačka v Brokeri.</span>
            </div>
            <div>
              <strong>Sadzba pre web</strong>
              <span>Prázdna hodnota automaticky použije základnú sadzbu.</span>
            </div>
            <div>
              <strong>Akciová v porovnaní</strong>
              <span>Prázdna hodnota použije sadzbu pre web.</span>
            </div>
          </div>

          <div class="history-note">
            <span class="history-note-icon">↗</span>
            <span>
              Každé uloženie skutočnej zmeny vytvorí historický bod s časom a
              administrátorom. Dáta tak budú pripravené pre budúce grafy vývoja sadzieb.
            </span>
          </div>

          <div class="filters">
            <label class="filter-field search-field">
              <span>Hľadať</span>
              <input
                v-model.trim="search"
                type="search"
                placeholder="Banka, účel alebo fixácia"
              >
            </label>

            <label class="filter-field">
              <span>Banka</span>
              <select v-model="selectedBank">
                <option value="all">Všetky banky</option>
                <option
                  v-for="bank in bankOptions"
                  :key="bank.code"
                  :value="bank.code"
                >
                  {{ bank.name }}
                </option>
              </select>
            </label>

            <label class="filter-field">
              <span>Účel</span>
              <select v-model="selectedPurpose">
                <option value="all">Všetky účely</option>
                <option
                  v-for="purpose in purposeOptions"
                  :key="purpose.value"
                  :value="purpose.value"
                >
                  {{ purpose.label }}
                </option>
              </select>
            </label>
          </div>

          <div v-if="loading" class="page-state">
            <v-progress-circular class="loading-spinner small" indeterminate />
            <span>Načítavam sadzby...</span>
          </div>

          <div v-else-if="loadError" class="page-state error-state">
            <strong>Sadzby sa nepodarilo načítať.</strong>
            <span>Skontrolujte pripojenie alebo nasadenie API a skúste to znova.</span>
            <button class="secondary-button" type="button" @click="loadRates">
              Skúsiť znova
            </button>
          </div>

          <template v-else>
            <div v-if="lastUpdatedLabel" class="last-updated">
              Posledná zmena v API: {{ lastUpdatedLabel }}
            </div>

            <section
              v-for="group in groupedRates"
              :key="group.code"
              class="bank-group"
            >
              <div class="bank-heading">
                <div class="bank-monogram">{{ bankMonogram(group.name) }}</div>
                <div>
                  <h3>{{ group.name }}</h3>
                  <span>{{ group.rates.length }} {{ rateCountLabel(group.rates.length) }}</span>
                </div>
              </div>

              <div class="rate-table-header" aria-hidden="true">
                <span>Účel</span>
                <span>Fixácia</span>
                <span>Základná</span>
                <span>Pre web</span>
                <span>V porovnaní</span>
                <span>Web</span>
                <span></span>
              </div>

              <div
                v-for="rate in group.rates"
                :key="rate.id"
                class="rate-row"
                :class="{
                  dirty: isRateDirty(rate),
                  invalid: Boolean(rateValidationError(rate)),
                  'hidden-rate': !rate.visible_on_website,
                }"
              >
                <div class="rate-purpose" data-label="Účel">
                  <strong>{{ rate.purpose_label }}</strong>
                  <small v-if="isRateDirty(rate)">Neuložená zmena</small>
                </div>

                <div class="fixation-cell" data-label="Fixácia">
                  FIX {{ rate.fixation_years }}
                </div>

                <label class="rate-input" data-label="Základná sadzba">
                  <span class="sr-only">Základná sadzba</span>
                  <span class="number-input">
                    <input
                      v-model="rate.base_rate"
                      type="number"
                      min="0"
                      max="99.99"
                      step="0.01"
                      inputmode="decimal"
                    >
                    <span>%</span>
                  </span>
                </label>

                <label class="rate-input" data-label="Sadzba pre web">
                  <span class="sr-only">Sadzba pre web</span>
                  <span class="number-input">
                    <input
                      v-model="rate.website_rate"
                      type="number"
                      min="0"
                      max="99.99"
                      step="0.01"
                      inputmode="decimal"
                      placeholder="dediť"
                    >
                    <span>%</span>
                  </span>
                  <small>Dedené: {{ effectiveWebsiteRate(rate) }} %</small>
                </label>

                <label class="rate-input" data-label="Akciová v porovnaní">
                  <span class="sr-only">Akciová sadzba v porovnaní</span>
                  <span class="number-input">
                    <input
                      v-model="rate.comparison_rate"
                      type="number"
                      min="0"
                      max="99.99"
                      step="0.01"
                      inputmode="decimal"
                      placeholder="dediť"
                    >
                    <span>%</span>
                  </span>
                  <small>Dedené: {{ effectiveComparisonRate(rate) }} %</small>
                </label>

                <label class="visibility-toggle" data-label="Zobraziť na webe">
                  <input v-model="rate.visible_on_website" type="checkbox">
                  <span class="toggle-track"><span></span></span>
                  <small>{{ rate.visible_on_website ? "Zobrazená" : "Skrytá" }}</small>
                </label>

                <div class="row-action" data-label="Akcia">
                  <button
                    class="save-row-button"
                    type="button"
                    :disabled="!isRateDirty(rate) || Boolean(rateValidationError(rate)) || isSaving(rate.id)"
                    @click="saveRate(rate)"
                  >
                    {{ isSaving(rate.id) ? "Ukladám" : "Uložiť" }}
                  </button>
                  <small v-if="rateValidationError(rate)" class="validation-message">
                    {{ rateValidationError(rate) }}
                  </small>
                </div>
              </div>
            </section>

            <div v-if="!groupedRates.length" class="page-state empty-state">
              <strong>Nenašli sa žiadne sadzby.</strong>
              <span>Skúste zmeniť vyhľadávanie alebo filtre.</span>
              <button class="secondary-button" type="button" @click="clearFilters">
                Zrušiť filtre
              </button>
            </div>
          </template>
        </main>

        <admin-financiers-settings v-else-if="activeSection === 'financiers'" />
        <admin-feedback-settings v-else-if="activeSection === 'feedback'" />
      </div>
    </div>

    <div v-else class="page-state unauthorized-state">
      <strong>Táto sekcia je dostupná iba administrátorom.</strong>
    </div>
  </div>
</template>

<script>
import router from "@/router";
import AdminFinanciersSettings from "./AdminFinanciersSettings.vue";
import AdminFeedbackSettings from "./AdminFeedbackSettings.vue";
import {
  getAdminMortgageRates,
  patchAdminMortgageRate,
} from "@/services/brokerService";

const EDITABLE_FIELDS = [
  "base_rate",
  "website_rate",
  "comparison_rate",
  "visible_on_website",
];

export default {
  name: "KartaNastavenia",
  components: {
    AdminFeedbackSettings,
    AdminFinanciersSettings,
  },
  data: () => ({
    rates: [],
    originalRates: {},
    loading: true,
    loadError: false,
    savingIds: [],
    savingAll: false,
    search: "",
    selectedBank: "all",
    selectedPurpose: "all",
  }),
  computed: {
    activeSection () {
      const section = this.$route.query.section;
      return ['rates', 'financiers', 'feedback'].includes(section) ? section : 'rates';
    },
    isAdmin () {
      return this.$store.getters["userState/getRole"] === "alex_admin";
    },
    bankOptions () {
      const banks = new Map();
      this.rates.forEach((rate) => {
        banks.set(rate.bank_code, {
          code: rate.bank_code,
          name: rate.bank_name,
        });
      });
      return [...banks.values()].sort((a, b) => a.name.localeCompare(b.name, "sk"));
    },
    purposeOptions () {
      const purposes = new Map();
      this.rates.forEach((rate) => {
        purposes.set(rate.purpose, {
          value: rate.purpose,
          label: rate.purpose_label,
        });
      });
      return [...purposes.values()].sort((a, b) => a.label.localeCompare(b.label, "sk"));
    },
    filteredRates () {
      const query = normalizeText(this.search);
      return this.rates.filter((rate) => {
        if (this.selectedBank !== "all" && rate.bank_code !== this.selectedBank) return false;
        if (this.selectedPurpose !== "all" && rate.purpose !== this.selectedPurpose) return false;
        if (!query) return true;

        const haystack = normalizeText([
          rate.bank_name,
          rate.bank_code,
          rate.purpose_label,
          rate.purpose,
          `fix ${rate.fixation_years}`,
        ].join(" "));
        return haystack.includes(query);
      });
    },
    groupedRates () {
      const groups = new Map();
      this.filteredRates.forEach((rate) => {
        if (!groups.has(rate.bank_code)) {
          groups.set(rate.bank_code, {
            code: rate.bank_code,
            name: rate.bank_name,
            rates: [],
          });
        }
        groups.get(rate.bank_code).rates.push(rate);
      });

      return [...groups.values()]
        .map((group) => ({
          ...group,
          rates: group.rates.sort((a, b) => {
            const purposeOrder = a.purpose_label.localeCompare(b.purpose_label, "sk");
            return purposeOrder || a.fixation_years - b.fixation_years;
          }),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, "sk"));
    },
    dirtyRateIds () {
      return this.rates
        .filter((rate) => this.isRateDirty(rate))
        .map((rate) => rate.id);
    },
    hasInvalidDirtyRates () {
      return this.rates.some(
        (rate) => this.isRateDirty(rate) && Boolean(this.rateValidationError(rate)),
      );
    },
    lastUpdatedLabel () {
      const timestamps = this.rates
        .map((rate) => new Date(rate.modified).getTime())
        .filter(Number.isFinite);
      if (!timestamps.length) return "";

      return new Intl.DateTimeFormat("sk-SK", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(Math.max(...timestamps)));
    },
  },
  async created () {
    if (!this.isAdmin) {
      this.loading = false;
      await router.replace({name: "KartaHypoteky"});
      return;
    }
    await this.loadRates();
  },
  methods: {
    selectSection (section) {
      if (section !== this.activeSection) this.$router.push({name: 'KartaNastavenia', query: {section}});
    },
    async loadRates () {
      this.loading = true;
      this.loadError = false;
      try {
        const response = await getAdminMortgageRates();
        const rates = response.results || response;
        this.rates = (rates || []).map((rate) => ({...rate}));
        this.captureOriginalRates();
      } catch (error) {
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    captureOriginalRates () {
      const originals = {};
      this.rates.forEach((rate) => {
        originals[rate.id] = editableSnapshot(rate);
      });
      this.originalRates = originals;
    },
    isRateDirty (rate) {
      const original = this.originalRates[rate.id];
      if (!original) return false;
      return JSON.stringify(editableSnapshot(rate)) !== JSON.stringify(original);
    },
    isSaving (id) {
      return this.savingIds.includes(id);
    },
    rateValidationError (rate) {
      if (!isValidRate(rate.base_rate, true)) return "Vyplňte sadzbu od 0 do 99,99 %.";
      if (!isValidRate(rate.website_rate)) return "Sadzba pre web nie je platná.";
      if (!isValidRate(rate.comparison_rate)) return "Akciová sadzba nie je platná.";
      return "";
    },
    effectiveWebsiteRate (rate) {
      return formatRate(isBlank(rate.website_rate) ? rate.base_rate : rate.website_rate);
    },
    effectiveComparisonRate (rate) {
      const value = isBlank(rate.comparison_rate)
        ? (isBlank(rate.website_rate) ? rate.base_rate : rate.website_rate)
        : rate.comparison_rate;
      return formatRate(value);
    },
    async saveRate (rate, options = {}) {
      if (!this.isRateDirty(rate)) return true;
      const validationError = this.rateValidationError(rate);
      if (validationError) {
        if (!options.silent) this.notify(validationError, "error");
        return false;
      }

      this.savingIds.push(rate.id);
      try {
        const savedRate = await patchAdminMortgageRate(
          rate.id,
          editablePayload(rate),
        );
        const index = this.rates.findIndex((item) => item.id === rate.id);
        if (index >= 0) this.$set(this.rates, index, {...savedRate});
        this.$set(this.originalRates, rate.id, editableSnapshot(savedRate));
        if (!options.silent) {
          this.notify(
            `${savedRate.bank_name} – FIX ${savedRate.fixation_years} bol uložený.`,
            "success",
          );
        }
        return true;
      } catch (error) {
        if (!options.silent) {
          this.notify("Sadzbu sa nepodarilo uložiť. Skúste to znova.", "error");
        }
        return false;
      } finally {
        this.savingIds = this.savingIds.filter((id) => id !== rate.id);
      }
    },
    async saveAll () {
      const dirtyRates = this.rates.filter((rate) => this.isRateDirty(rate));
      if (!dirtyRates.length || this.hasInvalidDirtyRates) return;

      this.savingAll = true;
      const results = await Promise.all(
        dirtyRates.map((rate) => this.saveRate(rate, {silent: true})),
      );
      this.savingAll = false;

      const savedCount = results.filter(Boolean).length;
      if (savedCount === dirtyRates.length) {
        this.notify(
          `${savedCount} ${rateCountLabelText(savedCount)} ${savedCount === 1 ? "bola uložená" : "bolo uložených"}.`,
          "success",
        );
      } else {
        this.notify(
          `Uložilo sa ${savedCount} z ${dirtyRates.length} sadzieb. Neuložené riadky skúste znova.`,
          "error",
        );
      }
    },
    clearFilters () {
      this.search = "";
      this.selectedBank = "all";
      this.selectedPurpose = "all";
    },
    notify (text, type) {
      this.$store.commit("appState/setSuccessMessage", {text, type});
    },
    bankMonogram (name) {
      return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase();
    },
    rateCountLabel (count) {
      if (count === 1) return "sadzba";
      if (count >= 2 && count <= 4) return "sadzby";
      return "sadzieb";
    },
  },
};

function isBlank (value) {
  return value === null || value === undefined || value === "";
}

function normalizeRate (value) {
  if (isBlank(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : value;
}

function isValidRate (value, required = false) {
  if (isBlank(value)) return !required;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 99.99;
}

function editableSnapshot (rate) {
  return EDITABLE_FIELDS.reduce((snapshot, field) => {
    snapshot[field] = field === "visible_on_website"
      ? Boolean(rate[field])
      : normalizeRate(rate[field]);
    return snapshot;
  }, {});
}

function editablePayload (rate) {
  const snapshot = editableSnapshot(rate);
  return {
    base_rate: Number(snapshot.base_rate).toFixed(2),
    website_rate: snapshot.website_rate === null
      ? null
      : Number(snapshot.website_rate).toFixed(2),
    comparison_rate: snapshot.comparison_rate === null
      ? null
      : Number(snapshot.comparison_rate).toFixed(2),
    visible_on_website: snapshot.visible_on_website,
  };
}

function formatRate (value) {
  const normalized = normalizeRate(value);
  return typeof normalized === "number"
    ? normalized.toLocaleString("sk-SK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    : "—";
}

function normalizeText (value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function rateCountLabelText (count) {
  if (count === 1) return "sadzba";
  if (count >= 2 && count <= 4) return "sadzby";
  return "sadzieb";
}
</script>

<style scoped lang="scss">
.settings-page {
  min-height: 100vh;
  padding: 38px 32px 140px;
  background:
    radial-gradient(circle at 85% 0, rgba(45, 125, 210, 0.12), transparent 30%),
    var(--app-bg, #eaf2fb);
  color: var(--app-text, #0a1953);
}

.settings-shell {
  width: min(1480px, 100%);
  margin: 0 auto;
}

.settings-header,
.card-heading,
.heading-actions,
.bank-heading,
.visibility-toggle {
  display: flex;
  align-items: center;
}

.settings-header {
  justify-content: space-between;
  gap: 28px;
  margin-bottom: 28px;
}

.eyebrow,
.section-kicker {
  color: var(--app-primary, #2d7dd2);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.settings-header h1 {
  margin: 5px 0 6px;
  font-size: clamp(30px, 4vw, 44px);
  line-height: 1.08;
}

.settings-header p,
.card-heading p {
  margin: 0;
  color: var(--app-text-muted, #6f7f96);
  font-size: 14px;
}

.admin-badge {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 9px;
  padding: 10px 14px;
  border: 1px solid rgba(36, 117, 76, 0.18);
  border-radius: 999px;
  background: var(--app-success-soft, #edf7f2);
  color: var(--app-success, #24754c);
  font-size: 12px;
  font-weight: 700;
}

.admin-badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}

.settings-layout {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.settings-navigation,
.settings-card {
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  background: var(--app-surface, #fff);
  box-shadow: var(--app-shadow, 0 8px 24px rgba(10, 25, 83, 0.06));
}

.settings-navigation {
  position: sticky;
  z-index: 8;
  top: 16px;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
  padding: 10px;
  border-radius: 18px;
}

.settings-navigation-item {
  position: relative;
  display: flex;
  min-width: 0;
  width: 100%;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: transparent;
  color: var(--app-text, #0a1953);
  text-align: left;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

button.settings-navigation-item {
  cursor: pointer;
}

button.settings-navigation-item:hover:not(.active) {
  border-color: var(--app-border, rgba(45, 125, 210, 0.16));
  background: var(--app-bg, #eaf2fb);
}

.settings-navigation-item.active {
  border-color: rgba(45, 125, 210, 0.22);
  background: var(--app-primary-soft, rgba(45, 125, 210, 0.12));
  color: var(--app-primary-hover, #2464a8);
}

.settings-navigation-item.active::after {
  position: absolute;
  right: 12px;
  bottom: 4px;
  left: 56px;
  height: 2px;
  border-radius: 999px;
  background: var(--app-primary, #2d7dd2);
  content: "";
}

.settings-navigation-item.disabled {
  opacity: 0.48;
}

.settings-navigation-item strong,
.settings-navigation-item small {
  display: block;
}

.settings-navigation-item strong {
  overflow: hidden;
  font-size: 12px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.settings-navigation-item small {
  margin-top: 2px;
  color: var(--app-text-muted, #6f7f96);
  font-size: 9px;
  line-height: 1.25;
}

.navigation-icon {
  display: grid;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  place-items: center;
  border-radius: 9px;
  background: var(--app-surface, #fff);
  color: var(--app-primary, #2d7dd2);
  font-size: 12px;
  font-weight: 900;
}

.settings-card {
  overflow: hidden;
  border-radius: 24px;
}

.card-heading {
  justify-content: space-between;
  gap: 24px;
  padding: 26px 28px 22px;
  border-bottom: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
}

.card-heading h2 {
  margin: 3px 0 5px;
  font-size: 24px;
}

.heading-actions {
  flex: 0 0 auto;
  gap: 9px;
}

.primary-button,
.secondary-button,
.save-row-button {
  min-height: 40px;
  padding: 0 15px;
  border: 0;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.primary-button {
  background: linear-gradient(135deg, var(--app-primary, #2d7dd2), #1854a8);
  color: #fff;
  box-shadow: 0 8px 18px rgba(45, 125, 210, 0.2);
}

.secondary-button {
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  background: var(--app-surface-muted, #f4f8fc);
  color: var(--app-text, #0a1953);
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.48;
  box-shadow: none;
}

.rate-guide {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: var(--app-border, rgba(45, 125, 210, 0.16));
  border-bottom: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
}

.rate-guide > div {
  padding: 16px 20px;
  background: var(--app-surface-muted, #f4f8fc);
}

.rate-guide strong,
.rate-guide span {
  display: block;
}

.rate-guide strong {
  margin-bottom: 3px;
  font-size: 12px;
}

.rate-guide span {
  color: var(--app-text-muted, #6f7f96);
  font-size: 10px;
  line-height: 1.5;
}

.filters {
  display: grid;
  grid-template-columns: minmax(230px, 1.4fr) minmax(160px, 0.8fr) minmax(170px, 0.8fr);
  gap: 12px;
  padding: 20px 28px;
}

.history-note {
  display: flex;
  gap: 10px;
  align-items: center;
  margin: 18px 28px 0;
  padding: 11px 13px;
  border: 1px solid rgba(36, 117, 76, 0.16);
  border-radius: 11px;
  background: var(--app-success-soft, #edf7f2);
  color: var(--app-success, #24754c);
  font-size: 10px;
  font-weight: 600;
  line-height: 1.5;
}

.history-note-icon {
  display: grid;
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  place-items: center;
  border-radius: 8px;
  background: rgba(36, 117, 76, 0.1);
  font-weight: 900;
}

.filter-field {
  display: grid;
  gap: 6px;
}

.filter-field > span {
  color: var(--app-text-muted, #6f7f96);
  font-size: 10px;
  font-weight: 700;
}

.filter-field input,
.filter-field select,
.number-input {
  width: 100%;
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  border-radius: 10px;
  background: var(--app-surface, #fff);
  color: var(--app-text, #0a1953);
}

.filter-field input,
.filter-field select {
  height: 42px;
  padding: 0 12px;
  outline: none;
  font-size: 12px;
}

.filter-field input:focus,
.filter-field select:focus,
.number-input:focus-within {
  border-color: var(--app-primary, #2d7dd2);
  box-shadow: 0 0 0 3px var(--app-primary-soft, rgba(45, 125, 210, 0.12));
}

.last-updated {
  padding: 0 28px 12px;
  color: var(--app-text-muted, #6f7f96);
  font-size: 10px;
  text-align: right;
}

.bank-group {
  margin: 0 28px 22px;
  overflow: hidden;
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  border-radius: 17px;
}

.bank-heading {
  gap: 11px;
  padding: 15px 17px;
  background: var(--app-surface-muted, #f4f8fc);
}

.bank-monogram {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border-radius: 11px;
  background: var(--app-primary-soft, rgba(45, 125, 210, 0.12));
  color: var(--app-primary-hover, #2464a8);
  font-size: 11px;
  font-weight: 900;
}

.bank-heading h3 {
  margin: 0;
  font-size: 14px;
}

.bank-heading span {
  color: var(--app-text-muted, #6f7f96);
  font-size: 10px;
}

.rate-table-header,
.rate-row {
  display: grid;
  grid-template-columns: minmax(125px, 1.1fr) 74px repeat(3, minmax(105px, 0.9fr)) 98px 102px;
  gap: 10px;
  align-items: center;
  padding: 11px 14px;
}

.rate-table-header {
  border-top: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  border-bottom: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  color: var(--app-text-muted, #6f7f96);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.rate-row {
  min-height: 76px;
  border-bottom: 1px solid var(--app-border, rgba(45, 125, 210, 0.12));
  background: var(--app-surface, #fff);
}

.rate-row:last-child {
  border-bottom: 0;
}

.rate-row.dirty {
  box-shadow: inset 3px 0 var(--app-primary, #2d7dd2);
}

.rate-row.invalid {
  box-shadow: inset 3px 0 var(--app-danger, #a63f3f);
}

.rate-row.hidden-rate {
  background: var(--app-surface-muted, #f4f8fc);
}

.rate-purpose strong,
.rate-purpose small {
  display: block;
}

.rate-purpose strong,
.fixation-cell {
  font-size: 11px;
  font-weight: 700;
}

.rate-purpose small {
  margin-top: 3px;
  color: var(--app-primary, #2d7dd2);
  font-size: 9px;
}

.fixation-cell {
  color: var(--app-primary-hover, #2464a8);
}

.rate-input {
  display: grid;
  gap: 3px;
}

.number-input {
  display: flex;
  align-items: center;
  height: 38px;
  padding-right: 9px;
  overflow: hidden;
}

.number-input input {
  width: 100%;
  min-width: 0;
  height: 100%;
  padding: 0 4px 0 10px;
  border: 0;
  background: transparent !important;
  color: inherit;
  font-size: 12px;
  font-weight: 700;
  outline: none;
  -moz-appearance: textfield;
}

.number-input input::-webkit-outer-spin-button,
.number-input input::-webkit-inner-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

.number-input > span {
  color: var(--app-text-muted, #6f7f96);
  font-size: 10px;
}

.rate-input small {
  color: var(--app-text-muted, #6f7f96);
  font-size: 8px;
}

.visibility-toggle {
  position: relative;
  flex-wrap: wrap;
  gap: 7px;
  cursor: pointer;
}

.visibility-toggle input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.toggle-track {
  position: relative;
  width: 34px;
  height: 20px;
  border-radius: 999px;
  background: #a7b4c5;
  transition: background 0.2s ease;
}

.toggle-track > span {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 5px rgba(10, 25, 83, 0.2);
  transition: transform 0.2s ease;
}

.visibility-toggle input:checked + .toggle-track {
  background: var(--app-success, #24754c);
}

.visibility-toggle input:checked + .toggle-track > span {
  transform: translateX(14px);
}

.visibility-toggle small {
  color: var(--app-text-muted, #6f7f96);
  font-size: 9px;
}

.save-row-button {
  min-height: 36px;
  width: 100%;
  background: var(--app-primary-soft, rgba(45, 125, 210, 0.12));
  color: var(--app-primary-hover, #2464a8);
}

.row-action {
  display: grid;
  gap: 4px;
}

.validation-message {
  color: var(--app-danger, #a63f3f);
  font-size: 8px;
  line-height: 1.3;
}

.page-state {
  display: flex;
  min-height: 240px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 30px;
  color: var(--app-text-muted, #6f7f96);
  text-align: center;
}

.page-state strong {
  color: var(--app-text, #0a1953);
}

.page-state .secondary-button {
  margin-top: 5px;
}

.unauthorized-state {
  min-height: calc(100vh - 200px);
}

.sr-only {
  position: absolute;
  overflow: hidden;
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

@media screen and (max-width: 1180px) {
  .settings-navigation {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scrollbar-width: thin;
  }

  .settings-navigation-item {
    flex: 0 0 205px;
    min-width: 205px;
  }

  .rate-table-header,
  .rate-row {
    grid-template-columns: minmax(120px, 1.1fr) 65px repeat(3, minmax(96px, 0.9fr)) 90px 90px;
    gap: 7px;
  }
}

@media screen and (max-width: 900px) {
  .settings-page {
    padding: 24px 18px 140px;
  }

  .settings-header,
  .card-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .heading-actions {
    width: 100%;
  }

  .heading-actions button {
    flex: 1;
  }

  .rate-guide {
    grid-template-columns: 1fr;
  }

  .filters {
    grid-template-columns: 1fr;
  }

  .rate-table-header {
    display: none;
  }

  .rate-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    padding: 17px;
  }

  .rate-row > [data-label]::before {
    display: block;
    margin-bottom: 5px;
    color: var(--app-text-muted, #6f7f96);
    content: attr(data-label);
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .visibility-toggle {
    align-content: start;
  }
}

@media screen and (max-width: 560px) {
  .settings-page {
    padding-right: 12px;
    padding-left: 12px;
  }

  .admin-badge {
    white-space: normal;
  }

  .settings-navigation-item {
    flex-basis: 175px;
    min-width: 175px;
  }

  .card-heading,
  .filters,
  .history-note {
    padding-right: 18px;
    padding-left: 18px;
  }

  .history-note {
    margin-right: 18px;
    margin-left: 18px;
  }

  .bank-group {
    margin-right: 12px;
    margin-left: 12px;
  }

  .rate-row {
    grid-template-columns: 1fr;
  }
}

</style>
